import { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import WaitlistForm from "../ui/WaitlistForm";

gsap.registerPlugin(ScrollTrigger);

// ── Countdown ─────────────────────────────────────────────────────────────────

const LAUNCH_DATE = new Date("2026-04-14T23:59:59");
const TOTAL_SEC   = (LAUNCH_DATE - new Date("2026-01-01T00:00:00")) / 1000;

function calcTimeLeft() {
  const diff = LAUNCH_DATE - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days:    Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours:   Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

function CountBlock({ value, label }) {
  const display = String(value).padStart(2, "0");
  const [prev, setPrev]     = useState(display);
  const [flipping, setFlip] = useState(false);
  const [hovered, setHov]   = useState(false);

  useEffect(() => {
    if (display !== prev) {
      setFlip(true);
      const t = setTimeout(() => { setPrev(display); setFlip(false); }, 340);
      return () => clearTimeout(t);
    }
  }, [display, prev]);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 9 }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <div style={{
        position: "relative",
        width: "clamp(64px, 8vw, 96px)",
        height: "clamp(72px, 9vw, 108px)",
        borderRadius: 16,
        background: hovered ? "rgba(16,183,127,0.08)" : "rgba(6,6,10,0.9)",
        border: `1px solid ${hovered ? "rgba(16,183,127,0.45)" : "rgba(16,183,127,0.18)"}`,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        boxShadow: hovered
          ? "0 0 40px rgba(16,183,127,0.18), 0 12px 40px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(16,183,127,0.08)"
          : "0 8px 32px rgba(0,0,0,0.6), 0 0 16px rgba(16,183,127,0.05), inset 0 0 0 1px rgba(255,255,255,0.04)",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
        transform: hovered ? "translateY(-4px) scale(1.06)" : "translateY(0) scale(1)",
        transition: "all 0.35s cubic-bezier(0.16,1,0.3,1)",
        cursor: "default",
      }}>
        {/* top glow line */}
        <div style={{
          position: "absolute", top: 0, left: "8%", right: "8%", height: 1,
          background: `linear-gradient(90deg, transparent, rgba(16,183,127,${hovered ? 0.85 : 0.35}), transparent)`,
          transition: "opacity 0.3s",
        }} />
        {/* horizontal split line */}
        <div style={{
          position: "absolute", left: 0, right: 0, top: "50%",
          height: 1, background: "rgba(0,0,0,0.5)", zIndex: 2,
        }} />
        {/* bottom accent */}
        <div style={{
          position: "absolute", bottom: 0, left: "20%", right: "20%", height: 2,
          borderRadius: "2px 2px 0 0",
          background: hovered ? "linear-gradient(90deg,transparent,rgba(16,183,127,0.75),transparent)" : "transparent",
          transition: "all 0.35s",
        }} />
        {/* top half sheen */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: "48%",
          background: "rgba(255,255,255,0.025)",
          borderRadius: "16px 16px 0 0",
          pointerEvents: "none",
        }} />
        <span style={{
          fontFamily: "Space Grotesk, sans-serif",
          fontWeight: 800,
          fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
          letterSpacing: "-0.05em",
          color: hovered ? "#fff" : "#F0EEF6",
          textShadow: hovered
            ? "0 0 28px rgba(16,183,127,0.7), 0 0 56px rgba(16,183,127,0.28)"
            : "0 0 18px rgba(16,183,127,0.28)",
          transform: flipping ? "translateY(-6px) rotateX(20deg)" : "translateY(0) rotateX(0deg)",
          opacity: flipping ? 0.4 : 1,
          transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.3s, color 0.3s, text-shadow 0.3s",
          position: "relative", zIndex: 3, display: "block",
        }}>
          {prev}
        </span>
      </div>
      <span style={{
        fontFamily: "Space Grotesk, sans-serif",
        fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.2em",
        textTransform: "uppercase",
        color: hovered ? "#10B77F" : "rgba(255,255,255,0.35)",
        transition: "color 0.3s",
      }}>{label}</span>
    </div>
  );
}

function Colon() {
  return (
    <div style={{
      display: "flex", flexDirection: "column",
      gap: "clamp(5px,1vw,9px)", paddingBottom: 20, alignSelf: "center",
    }}>
      {[0, 1].map(i => (
        <div key={i} style={{
          width: 5, height: 5, borderRadius: "50%",
          background: "rgba(16,183,127,0.5)",
          boxShadow: "0 0 8px rgba(16,183,127,0.5)",
          animation: "pulseGold 1.8s ease-in-out infinite",
          animationDelay: `${i * 0.35}s`,
        }} />
      ))}
    </div>
  );
}

// ── Hooks ──────────────────────────────────────────────────────────────────────

function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    const fn = () => setY(window.scrollY);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return y;
}

// ── Floating stat card ───────────────────────────────────────────────────────

function HeroFloatCard({ icon, label, value, valueColor, rotation, delay }) {
  return (
    <div style={{
      transform: `rotate(${rotation})`,
      animation: `floatCard 5s ease-in-out ${delay} infinite`,
      "--fc-r": rotation,
    }}>
      <div style={{
        background: "rgba(8,11,22,0.88)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 14, padding: "10px 16px",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        boxShadow: `0 20px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.05), 0 0 20px ${valueColor}18`,
        display: "flex", alignItems: "center", gap: 11, whiteSpace: "nowrap",
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: `${valueColor}15`, border: `1px solid ${valueColor}30`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.05rem", flexShrink: 0,
        }}>{icon}</div>
        <div>
          <div style={{
            fontSize: "0.63rem", color: "rgba(255,255,255,0.38)",
            fontFamily: "Space Grotesk, sans-serif", marginBottom: 2,
          }}>{label}</div>
          <div style={{
            fontSize: "0.94rem", fontWeight: 800,
            color: valueColor, fontFamily: "Space Grotesk, sans-serif",
            textShadow: `0 0 16px ${valueColor}60`,
          }}>{value}</div>
        </div>
      </div>
    </div>
  );
}

// ── Wide Dashboard Mockup ─────────────────────────────────────────────────────

const HOLDINGS = [
  { ticker: "AAPL", pct: 42, value: "$35,402", color: "#60A5FA" },
  { ticker: "NVDA", pct: 28, value: "$23,601", color: "#10B77F" },
  { ticker: "MSFT", pct: 18, value: "$15,172", color: "#A78BFA" },
  { ticker: "ETFs", pct: 12, value: "$10,115", color: "#FBBF24" },
];

const SIGNALS = [
  { ticker: "AAPL", action: "BUY",  confidence: 91, change: "+2.4%" },
  { ticker: "NVDA", action: "BUY",  confidence: 88, change: "+5.2%" },
  { ticker: "TSLA", action: "HOLD", confidence: 72, change: "+0.8%" },
  { ticker: "META", action: "BUY",  confidence: 84, change: "+3.1%" },
];

function WideDashboard() {
  return (
    <div style={{
      background: "#080A12",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 16,
      overflow: "hidden",
      boxShadow:
        "0 0 0 1px rgba(255,255,255,0.04)," +
        "0 40px 120px rgba(0,0,0,0.8)," +
        "0 0 80px rgba(16,183,127,0.06)",
    }}>

      {/* Chrome */}
      <div style={{
        background: "rgba(0,0,0,0.35)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        padding: "10px 16px",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <div style={{ display: "flex", gap: 6 }}>
          {["#FF5F57","#FEBC2E","#28C840"].map(c => (
            <div key={c} style={{ width: 9, height: 9, borderRadius: "50%", background: c, opacity: 0.75 }} />
          ))}
        </div>
        <div style={{
          flex: 1, background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: 6, padding: "4px 12px", textAlign: "center",
          fontSize: "0.65rem", color: "rgba(255,255,255,0.22)",
          fontFamily: "Space Grotesk, sans-serif",
        }}>
          app.alphastack.ai/dashboard
        </div>
        <span style={{
          fontSize: "0.57rem", fontWeight: 700, padding: "2px 7px",
          borderRadius: 4, background: "rgba(16,183,127,0.1)",
          border: "1px solid rgba(16,183,127,0.2)",
          color: "#10B77F", fontFamily: "Space Grotesk, sans-serif",
          letterSpacing: "0.05em",
        }}>LIVE</span>
      </div>

      {/* Dashboard body: 3-panel layout */}
      <div style={{ display: "grid", gridTemplateColumns: "200px 1fr 190px", gap: 0 }}>

        {/* ── Left sidebar ── */}
        <div style={{
          borderRight: "1px solid rgba(255,255,255,0.05)",
          padding: "16px 14px",
        }}>
          <div style={{
            fontSize: "0.55rem", letterSpacing: "0.12em", textTransform: "uppercase",
            color: "rgba(255,255,255,0.22)", fontFamily: "Space Grotesk, sans-serif",
            marginBottom: 12,
          }}>Navigation</div>
          {[
            ["📊", "Dashboard",   true ],
            ["🤖", "AI Signals",  false],
            ["📈", "Portfolio",   false],
            ["🌐", "Market",      false],
            ["⚙️", "Settings",    false],
          ].map(([icon, label, active]) => (
            <div key={label} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "6px 9px", borderRadius: 7, marginBottom: 2,
              background: active ? "rgba(16,183,127,0.1)" : "transparent",
              border: active ? "1px solid rgba(16,183,127,0.18)" : "1px solid transparent",
            }}>
              <span style={{ fontSize: "0.7rem" }}>{icon}</span>
              <span style={{
                fontSize: "0.71rem", fontWeight: active ? 600 : 400,
                color: active ? "#10B77F" : "rgba(255,255,255,0.35)",
                fontFamily: "Space Grotesk, sans-serif",
              }}>{label}</span>
            </div>
          ))}

          {/* Portfolio value */}
          <div style={{
            marginTop: 20, padding: "12px 10px",
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.05)",
            borderRadius: 10,
          }}>
            <div style={{
              fontSize: "0.54rem", color: "rgba(255,255,255,0.28)",
              fontFamily: "Space Grotesk, sans-serif",
              textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5,
            }}>Total Value</div>
            <div style={{
              fontSize: "1.25rem", fontWeight: 800, color: "#F0EEF6",
              fontFamily: "Space Grotesk, sans-serif", letterSpacing: "-0.03em",
              lineHeight: 1, marginBottom: 5,
            }}>$84,291</div>
            <div style={{
              fontSize: "0.69rem", fontWeight: 700, color: "#10B77F",
              background: "rgba(16,183,127,0.1)",
              border: "1px solid rgba(16,183,127,0.18)",
              display: "inline-block", padding: "2px 7px", borderRadius: 5,
              fontFamily: "Space Grotesk, sans-serif",
            }}>↑ +18.4%</div>
          </div>
        </div>

        {/* ── Center: chart + holdings ── */}
        <div style={{ padding: "16px 18px" }}>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            marginBottom: 12,
          }}>
            <div style={{
              fontSize: "0.7rem", fontWeight: 600,
              color: "rgba(255,255,255,0.55)", fontFamily: "Space Grotesk, sans-serif",
            }}>Portfolio Performance</div>
            <div style={{ display: "flex", gap: 4 }}>
              {["1D","1W","1M","1Y"].map((t, i) => (
                <button key={t} style={{
                  fontSize: "0.6rem", padding: "2px 8px", borderRadius: 4,
                  background: i === 2 ? "rgba(16,183,127,0.12)" : "transparent",
                  border: i === 2 ? "1px solid rgba(16,183,127,0.22)" : "1px solid rgba(255,255,255,0.07)",
                  color: i === 2 ? "#10B77F" : "rgba(255,255,255,0.28)",
                  cursor: "pointer", fontFamily: "Space Grotesk, sans-serif",
                }}>{t}</button>
              ))}
            </div>
          </div>

          {/* Sparkline */}
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.05)",
            borderRadius: 10, padding: "10px 12px", marginBottom: 14,
          }}>
            <svg width="100%" height="64" viewBox="0 0 400 64" preserveAspectRatio="none">
              <defs>
                <linearGradient id="hv2grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B77F" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#10B77F" stopOpacity="0"   />
                </linearGradient>
              </defs>
              <path d="M0,64 L0,52 C25,46 48,50 72,42 C96,34 120,38 148,29 C176,20 200,24 228,15 C256,7 280,11 308,6 C336,2 362,3 392,1 L400,0 L400,64 Z" fill="url(#hv2grad)" />
              <path d="M0,52 C25,46 48,50 72,42 C96,34 120,38 148,29 C176,20 200,24 228,15 C256,7 280,11 308,6 C336,2 362,3 392,1 L400,0"
                fill="none" stroke="#10B77F" strokeWidth="1.8" strokeLinecap="round" />
              <circle cx="400" cy="0" r="4" fill="#10B77F" />
              <circle cx="400" cy="0" r="8" fill="#10B77F" fillOpacity="0.2" />
            </svg>
          </div>

          {/* Holdings */}
          <div style={{
            fontSize: "0.59rem", color: "rgba(255,255,255,0.24)",
            textTransform: "uppercase", letterSpacing: "0.1em",
            fontFamily: "Space Grotesk, sans-serif", marginBottom: 9,
          }}>Holdings</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {HOLDINGS.map(({ ticker, pct, value, color }) => (
              <div key={ticker} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  width: 38, fontSize: "0.7rem", fontWeight: 700,
                  color: "#F0EEF6", fontFamily: "Space Grotesk, sans-serif",
                }}>{ticker}</span>
                <div style={{ flex: 1, height: 3, borderRadius: 99, background: "rgba(255,255,255,0.05)", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${pct}%`, borderRadius: 99, background: color, boxShadow: `0 0 7px ${color}70` }} />
                </div>
                <span style={{
                  fontSize: "0.65rem", color: "rgba(255,255,255,0.36)",
                  fontFamily: "Space Grotesk, sans-serif", width: 54, textAlign: "right",
                }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right: AI Signals ── */}
        <div style={{
          borderLeft: "1px solid rgba(255,255,255,0.05)",
          padding: "16px 14px",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6, marginBottom: 12,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%", background: "#10B77F",
              animation: "pulseGold 1.6s ease-in-out infinite", flexShrink: 0,
            }} />
            <span style={{
              fontSize: "0.59rem", color: "rgba(255,255,255,0.28)",
              fontFamily: "Space Grotesk, sans-serif",
              textTransform: "uppercase", letterSpacing: "0.1em",
            }}>AI Signals</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {SIGNALS.map(({ ticker, action, confidence, change }) => (
              <div key={ticker} style={{
                background: "rgba(255,255,255,0.025)",
                border: "1px solid rgba(255,255,255,0.05)",
                borderRadius: 9, padding: "8px 10px",
              }}>
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  marginBottom: 5,
                }}>
                  <span style={{
                    fontSize: "0.72rem", fontWeight: 700, color: "#F0EEF6",
                    fontFamily: "Space Grotesk, sans-serif",
                  }}>{ticker}</span>
                  <span style={{
                    fontSize: "0.56rem", fontWeight: 700, letterSpacing: "0.05em",
                    padding: "2px 6px", borderRadius: 999,
                    background: action === "BUY" ? "rgba(16,183,127,0.12)" : "rgba(251,191,36,0.12)",
                    border: `1px solid ${action === "BUY" ? "rgba(16,183,127,0.25)" : "rgba(251,191,36,0.25)"}`,
                    color: action === "BUY" ? "#10B77F" : "#FBBF24",
                  }}>{action}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{
                    flex: 1, height: 2, borderRadius: 99,
                    background: "rgba(255,255,255,0.06)", overflow: "hidden", marginRight: 8,
                  }}>
                    <div style={{
                      height: "100%", width: `${confidence}%`, borderRadius: 99,
                      background: action === "BUY" ? "#10B77F" : "#FBBF24",
                    }} />
                  </div>
                  <span style={{
                    fontSize: "0.65rem", fontWeight: 700, color: "#10B77F",
                    fontFamily: "Space Grotesk, sans-serif",
                  }}>{change}</span>
                </div>
                <div style={{
                  fontSize: "0.54rem", color: "rgba(255,255,255,0.22)",
                  fontFamily: "Space Grotesk, sans-serif", marginTop: 3,
                }}>
                  Confidence: {confidence}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Hero V2 ───────────────────────────────────────────────────────────────────

export default function HeroV2() {
  const [mounted, setMounted] = useState(false);
  const [time, setTime]       = useState(calcTimeLeft);
  const scrollY = useScrollY(); // still used for background parallax only

  // GSAP refs
  const dashWrapRef = useRef(null);
  const dashInnerRef = useRef(null);
  const card1Ref    = useRef(null);
  const card2Ref    = useRef(null);
  const card3Ref    = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setTime(calcTimeLeft()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  // GSAP ScrollTrigger — 3D animations
  useEffect(() => {
    if (!mounted) return;
    const ctx = gsap.context(() => {

      // ① Dashboard: tilts from 10° → 0° rotateX as it enters the viewport
      gsap.fromTo(dashInnerRef.current,
        { rotateX: 10, scale: 0.93, transformOrigin: "center top" },
        {
          rotateX: 0, scale: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: dashWrapRef.current,
            start: "top 90%",
            end: "top 15%",
            scrub: 1.4,
          },
        }
      );

      // ② Dashboard: gentle rotateY oscillation as user scrolls further
      gsap.fromTo(dashInnerRef.current,
        { rotateY: -4 },
        {
          rotateY: 4,
          ease: "sine.inOut",
          scrollTrigger: {
            trigger: dashWrapRef.current,
            start: "top 15%",
            end: "bottom top",
            scrub: 2,
          },
        }
      );

      // ③ Floating card parallax — each at a different speed
      if (card1Ref.current) {
        gsap.to(card1Ref.current, {
          y: -90,
          ease: "none",
          scrollTrigger: {
            trigger: dashWrapRef.current,
            start: "top 80%",
            end: "bottom top",
            scrub: 1,
          },
        });
      }
      if (card2Ref.current) {
        gsap.to(card2Ref.current, {
          y: -55,
          ease: "none",
          scrollTrigger: {
            trigger: dashWrapRef.current,
            start: "top 80%",
            end: "bottom top",
            scrub: 1.6,
          },
        });
      }
      if (card3Ref.current) {
        gsap.to(card3Ref.current, {
          y: -130,
          ease: "none",
          scrollTrigger: {
            trigger: dashWrapRef.current,
            start: "top 80%",
            end: "bottom top",
            scrub: 0.8,
          },
        });
      }
    });

    return () => ctx.revert();
  }, [mounted]);

  const leftSec = time.days * 86400 + time.hours * 3600 + time.minutes * 60 + time.seconds;
  const pct     = Math.min(100, Math.max(0, ((TOTAL_SEC - leftSec) / TOTAL_SEC) * 100));

  const fade = (delay) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? "translateY(0)" : "translateY(14px)",
    transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
  });

  return (
    <section style={{
      position: "relative",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "130px 24px 0",
      overflow: "hidden",
    }}>

      {/* ── Backgrounds ─────────────────────────────────── */}

      {/* Grid lines */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)," +
          "linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
        backgroundSize: "55px 55px",
        maskImage: "radial-gradient(ellipse 85% 70% at 50% 30%, black 30%, transparent 100%)",
        WebkitMaskImage: "radial-gradient(ellipse 85% 70% at 50% 30%, black 30%, transparent 100%)",
        transform: `translateY(${scrollY * 0.06}px)`,
      }} />

      {/* Primary green glow — top center */}
      <div style={{
        position: "absolute", top: -100, left: "50%",
        transform: `translateX(-50%) translateY(${scrollY * 0.1}px)`,
        width: 1000, height: 700, pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(16,183,127,0.22) 0%, transparent 60%)",
        filter: "blur(60px)",
      }} />

      {/* Blue glow — right */}
      <div style={{
        position: "absolute", top: "40%", right: "-10%", pointerEvents: "none",
        width: 600, height: 600,
        background: "radial-gradient(circle, rgba(96,165,250,0.14) 0%, transparent 65%)",
        filter: "blur(70px)",
        transform: `translateY(${-scrollY * 0.05}px)`,
      }} />

      {/* Purple glow — left */}
      <div style={{
        position: "absolute", top: "50%", left: "-8%", pointerEvents: "none",
        width: 550, height: 550,
        background: "radial-gradient(circle, rgba(167,139,250,0.13) 0%, transparent 65%)",
        filter: "blur(70px)",
        transform: `translateY(${-scrollY * 0.07}px)`,
      }} />

      {/* Gold accent — bottom center */}
      <div style={{
        position: "absolute", bottom: "10%", left: "50%",
        transform: `translateX(-50%) translateY(${-scrollY * 0.04}px)`,
        width: 700, height: 300, pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(16,183,127,0.12) 0%, transparent 70%)",
        filter: "blur(50px)",
      }} />

      {/* Bottom fade */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: 160,
        background: "linear-gradient(to bottom, transparent, #06060A)",
        pointerEvents: "none", zIndex: 2,
      }} />

      {/* ════════════════════════════════════════════════════
          PART 1 — Launch announcement + countdown
          ════════════════════════════════════════════════════ */}
      <div style={{
        position: "relative", zIndex: 1,
        textAlign: "center", maxWidth: 780, width: "100%",
        paddingBottom: 72,
      }}>

        {/* "Launching Soon" badge */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 7,
          padding: "4px 14px 4px 10px",
          background: "rgba(16,183,127,0.07)",
          border: "1px solid rgba(16,183,127,0.16)",
          borderRadius: 999, marginBottom: 28,
          ...fade(0.05),
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: "50%",
            background: "#10B77F", boxShadow: "0 0 8px #10B77F",
            animation: "pulseGold 2s ease infinite",
          }} />
          <span style={{
            fontSize: "0.73rem", fontWeight: 600, letterSpacing: "0.04em",
            color: "#10B77F", fontFamily: "Space Grotesk, sans-serif",
          }}>Early Access · Launching April 2026</span>
        </div>

        {/* "Alpha Stack\nIs Coming." */}
        <h1 style={{
          fontFamily: "Space Grotesk, sans-serif",
          fontWeight: 900, lineHeight: 1.05, letterSpacing: "-0.04em",
          color: "#FFFFFF", marginBottom: 20,
          fontSize: "clamp(3rem, 6.5vw, 5.4rem)",
          textShadow: "0 0 80px rgba(16,183,127,0.25)",
          ...fade(0.12),
        }}>
          Alpha Stack<br />
          <span style={{ color: "rgba(255,255,255,0.7)" }}>Is Coming.</span>
        </h1>

        {/* Description */}
        <p style={{
          fontSize: "clamp(0.95rem, 1.6vw, 1.1rem)",
          color: "rgba(255,255,255,0.58)",
          lineHeight: 1.75, maxWidth: 560, margin: "0 auto 48px",
          fontFamily: "Space Grotesk, sans-serif",
          ...fade(0.22),
        }}>
          Alpha Stack brings institutional-grade AI analysis to retail investors.
          Join early and get priority access on launch day.
        </p>

        {/* ⏱ Launching in label */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          fontSize: "0.63rem", fontWeight: 700, letterSpacing: "0.22em",
          textTransform: "uppercase", color: "#10B77F",
          fontFamily: "Space Grotesk, sans-serif",
          marginBottom: 22,
          ...fade(0.3),
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: "50%", background: "#10B77F",
            boxShadow: "0 0 8px #10B77F",
            animation: "pulseGold 1.8s ease-in-out infinite",
          }} />
          ⏱ Launching in
        </div>

        {/* Timer blocks */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: "clamp(8px, 1.5vw, 18px)",
          marginBottom: 28,
          ...fade(0.36),
        }}>
          <CountBlock value={time.days}    label="Days"  />
          <Colon />
          <CountBlock value={time.hours}   label="Hours" />
          <Colon />
          <CountBlock value={time.minutes} label="Mins"  />
          <Colon />
          <CountBlock value={time.seconds} label="Secs"  />
        </div>

        {/* Progress bar */}
        <div style={{
          maxWidth: 420, margin: "0 auto",
          ...fade(0.44),
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between", marginBottom: 7,
            fontFamily: "Space Grotesk, sans-serif",
            fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.1em",
            color: "rgba(255,255,255,0.28)", textTransform: "uppercase",
          }}>
            <span>Launch Progress</span>
            <span style={{ color: "#10B77F" }}>{Math.round(pct)}%</span>
          </div>
          <div style={{
            height: 5, borderRadius: 3,
            background: "rgba(255,255,255,0.05)",
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.04)",
          }}>
            <div style={{
              height: "100%", borderRadius: 3,
              width: `${pct}%`,
              background: "linear-gradient(90deg, #059669, #10B77F, #6EE7B7)",
              boxShadow: "0 0 14px rgba(16,183,127,0.5)",
              transition: "width 1s ease",
              position: "relative",
            }}>
              <div style={{
                position: "absolute", top: 0, right: 0, bottom: 0, width: 40,
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.32), transparent)",
                animation: "shimmerSlide 2s ease-in-out infinite",
              }} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Divider ──────────────────────────────────────── */}
      <div style={{
        position: "relative", zIndex: 1,
        width: "100%", maxWidth: 680,
        display: "flex", alignItems: "center", gap: 20,
        marginBottom: 72,
        ...fade(0.5),
      }}>
        <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.07))" }} />
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          fontSize: "0.62rem", fontWeight: 600, letterSpacing: "0.12em",
          textTransform: "uppercase", color: "rgba(255,255,255,0.2)",
          fontFamily: "Space Grotesk, sans-serif", whiteSpace: "nowrap",
        }}>
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: "rgba(255,255,255,0.15)" }} />
          What you're getting
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: "rgba(255,255,255,0.15)" }} />
        </div>
        <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, rgba(255,255,255,0.07), transparent)" }} />
      </div>

      {/* ════════════════════════════════════════════════════
          PART 2 — Product pitch + form
          ════════════════════════════════════════════════════ */}
      <div style={{
        position: "relative", zIndex: 1,
        textAlign: "center", maxWidth: 800, width: "100%",
      }}>

        {/* Gradient headline */}
        <h2 style={{
          fontFamily: "Space Grotesk, sans-serif",
          fontWeight: 900, lineHeight: 1.07, letterSpacing: "-0.04em",
          color: "#F0EEF6", marginBottom: 22,
          fontSize: "clamp(2.5rem, 5.5vw, 4.4rem)",
          ...fade(0.56),
        }}>
          Invest with the{" "}
          <span style={{
            background: "linear-gradient(135deg, #10B77F 0%, #60A5FA 55%, #A78BFA 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}>
            clarity of AI.
          </span>
        </h2>

        {/* Sub */}
        <p style={{
          fontSize: "clamp(0.95rem, 1.6vw, 1.1rem)",
          color: "rgba(255,255,255,0.56)",
          lineHeight: 1.75, maxWidth: 560, margin: "0 auto 36px",
          fontFamily: "Space Grotesk, sans-serif",
          ...fade(0.64),
        }}>
          Alpha Stack brings institutional-grade AI analysis to retail investors.
          Real-time signals, portfolio analytics, and macro intelligence —
          all in one focused dashboard.
        </p>

        {/* Form */}
        <div style={{ maxWidth: 380, margin: "0 auto 24px", ...fade(0.7) }}>
          <WaitlistForm variant="hero" />
        </div>

        {/* Social proof */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
          ...fade(0.76),
        }}>
          <div style={{ display: "flex" }}>
            {["#10B77F","#60A5FA","#A78BFA","#FBBF24","#F87171"].map((c, i) => (
              <div key={c} style={{
                width: 28, height: 28, borderRadius: "50%",
                background: `linear-gradient(135deg, ${c}55, ${c}18)`,
                border: "2px solid #06060A",
                marginLeft: i > 0 ? -8 : 0,
              }} />
            ))}
          </div>
          <span style={{
            fontSize: "0.82rem", color: "rgba(240,238,246,0.36)",
            fontFamily: "Space Grotesk, sans-serif",
          }}>
            <strong style={{ color: "rgba(240,238,246,0.72)", fontWeight: 700 }}>1,200+</strong>{" "}
            investors already on the waitlist
          </span>
        </div>
      </div>

      {/* ── 3D Scroll Dashboard (GSAP ScrollTrigger) ──────── */}
      <div
        ref={dashWrapRef}
        style={{
          position: "relative", zIndex: 1,
          width: "100%", maxWidth: 1100,
          marginTop: 64,
          perspective: "1400px",
          perspectiveOrigin: "50% 40%",
          opacity: mounted ? 1 : 0,
          transition: "opacity 0.9s ease 0.8s",
        }}
      >
        {/* GSAP animates rotateX / rotateY / scale on this div */}
        <div
          ref={dashInnerRef}
          style={{
            transformStyle: "preserve-3d",
            transformOrigin: "center top",
            position: "relative",
          }}
        >
          <WideDashboard />

          {/* Floating card 1 — Monthly Return */}
          <div ref={card1Ref} style={{ position: "absolute", top: -24, right: -28, zIndex: 10 }}>
            <HeroFloatCard
              icon="📈" label="Monthly Return" value="+18.4%"
              valueColor="#10B77F" rotation="-3deg" delay="0s"
              translateY={0} style={{}}
            />
          </div>

          {/* Floating card 2 — Live Signals */}
          <div ref={card2Ref} style={{ position: "absolute", bottom: 32, left: -28, zIndex: 10 }}>
            <HeroFloatCard
              icon="⚡" label="AI Signals Active" value="94 Live"
              valueColor="#60A5FA" rotation="3deg" delay="1.6s"
              translateY={0} style={{}}
            />
          </div>

          {/* Floating card 3 — Accuracy */}
          <div ref={card3Ref} style={{ position: "absolute", bottom: -16, right: 60, zIndex: 10 }}>
            <HeroFloatCard
              icon="🎯" label="Signal Accuracy" value="94.2%"
              valueColor="#A78BFA" rotation="-2deg" delay="0.9s"
              translateY={0} style={{}}
            />
          </div>
        </div>

        {/* Glows beneath the mockup */}
        <div style={{
          position: "absolute", bottom: -60, left: "50%",
          transform: "translateX(-50%)",
          width: "80%", height: 100, pointerEvents: "none",
          background: "radial-gradient(ellipse, rgba(16,183,127,0.3) 0%, transparent 70%)",
          filter: "blur(36px)",
        }} />
        <div style={{
          position: "absolute", bottom: -40, left: "20%",
          width: "25%", height: 60, pointerEvents: "none",
          background: "radial-gradient(ellipse, rgba(96,165,250,0.18) 0%, transparent 70%)",
          filter: "blur(24px)",
        }} />
        <div style={{
          position: "absolute", bottom: -40, right: "20%",
          width: "25%", height: 60, pointerEvents: "none",
          background: "radial-gradient(ellipse, rgba(167,139,250,0.16) 0%, transparent 70%)",
          filter: "blur(24px)",
        }} />
      </div>

    </section>
  );
}
